import React from 'react'
import {
  Bookmark,
  Speaker,
  Highlighter,
  CommentBubble,
  BackArrow,
} from './icons'
import { useArticle } from './ArticleProvider'
import { Progress } from '../../lib/ui/progress'
import { useTranslation } from '~/hooks/useTranslation'
import { apiPost } from '~/util/api'
import { useQueryClient, useMutation } from '@tanstack/react-query'

export const NavHeader = () => {
  const { cliftonStrengths, missingAudio, coachServicesArticle } = useArticle()

  return (
    <div className="sticky top-0 z-[1020] flex w-full flex-row border-b border-[#4056D4] bg-[#EAF4FF]">
      <div className="flex h-20 flex-grow flex-col items-center justify-center">
        <div className="flex max-w-5xl flex-row gap-1">
          {!cliftonStrengths && !coachServicesArticle && <Favorite />}
          {!cliftonStrengths && !missingAudio && <Audio />}
          <Highlight />
          <Comment />
        </div>
      </div>
      {cliftonStrengths ? (
        <BackToGuidesCliftonStrengths />
      ) : coachServicesArticle ? (
        <BackToCoachServices />
      ) : (
        <BackToResources />
      )}
    </div>
  )
}

export const AudioPlayer = () => {
  const { audioAwsUrl, audioActive, jsonContent } = useArticle()
  const moduleName = jsonContent.meta.module
  const slug = jsonContent.meta.slug

  let achievementModule = moduleName
  if (moduleName === 'resumes') {
    achievementModule = 'resume'
  } else if (moduleName === 'job_search') {
    achievementModule = 'job-search'
  } else if (moduleName === 'orientations') {
    achievementModule = 'orientation'
  } else if (moduleName === 'interviews') {
    achievementModule = 'interview'
  }
  const formattedSlug = slug.replace(/-/g, '_')
  const mutationFn = () => {
    const data = {
      achievement_module: achievementModule,
    }
    return apiPost(
      `/${achievementModule}/resources/audio/${formattedSlug}/send_achievement`,
      data,
    )
  }

  const queryClient = useQueryClient()

  const achievementMutation = useMutation({
    mutationFn,
    onSuccess: () => {
      queryClient.invalidateQueries(['resourceAudioAchievements'])
    },
  })

  if (!audioAwsUrl || !audioActive) {
    return
  }

  return (
    <div className="w-full">
      <audio
        className="w-full"
        controls
        src={audioAwsUrl}
        autoPlay={audioActive}
        onPlay={() => achievementMutation.mutate(formattedSlug)}
      />
    </div>
  )
}

export const RightRail = () => {
  return (
    <div className="tw-hidden sticky left-0 top-20 ml-4 w-[359px] flex-shrink-0 border-l p-4 lg:block">
      <div className="sticky left-0 top-20 h-max">
        <div className="flex flex-col gap-4 bg-white">
          <ProgressDisplay />
          <TableOfContents />
        </div>
      </div>
    </div>
  )
}

const BackToResources = () => {
  const { t } = useTranslation('Controls')
  const { navigateToResourceCenter } = useArticle()
  return (
    <div className="tw-hidden h-20 w-[359px] content-center pr-8 text-end lg:block">
      <button type="button" onClick={navigateToResourceCenter}>
        {t('back_to_resource_center')}
        <BackArrow className="mb-1 ml-2 inline stroke-black" />
      </button>
    </div>
  )
}

const BackToCoachServices = () => {
  const { coachServicesArticle } = useArticle()
  const { navigateToCoachServices } = useArticle()
  return (
    <div className="tw-hidden h-20 w-[359px] content-center pr-8 text-end lg:block">
      <button type="button" onClick={navigateToCoachServices}>
        {coachServicesArticle.back_to_resource}
        <BackArrow className="mb-1 ml-2 inline stroke-black" />
      </button>
    </div>
  )
}

const Favorite = () => {
  const { t } = useTranslation('Controls')
  const { isFavorite, toggleFavorite, jsonContent } = useArticle()
  const articleTitle = jsonContent?.title ?? jsonContent?.meta?.title ?? ''
  const label = isFavorite ? t('favorited') : t('favorite')
  const ariaLabel = articleTitle ? `${label}, ${articleTitle}` : label

  return (
    <ToolButton
      icon={<Bookmark active={isFavorite} />}
      onClick={toggleFavorite}
      active={isFavorite}
      aria-label={ariaLabel}
    >
      {label}
    </ToolButton>
  )
}

const Audio = () => {
  const { t } = useTranslation('Controls')
  const { audioActive, toggleAudioActive } = useArticle()

  const handleClick = () => {
    toggleAudioActive()
  }
  return (
    <ToolButton
      active={audioActive}
      icon={<Speaker active={audioActive} />}
      onClick={handleClick}
    >
      {t('audio')}
    </ToolButton>
  )
}

const Highlight = () => {
  const { t } = useTranslation('Controls')
  const { toggleHighlightMode, isHighlightModeActive } = useArticle()
  return (
    <ToolButton
      icon={<Highlighter active={isHighlightModeActive} />}
      active={isHighlightModeActive}
      onClick={toggleHighlightMode}
    >
      {isHighlightModeActive ? t('highlighting') : t('highlight')}
    </ToolButton>
  )
}

const Comment = () => {
  const { t } = useTranslation('Controls')
  const { isCommentModeActive, toggleCommentMode } = useArticle()

  return (
    <ToolButton
      active={isCommentModeActive}
      icon={<CommentBubble active={isCommentModeActive} />}
      onClick={toggleCommentMode}
    >
      {t('add_notes')}
    </ToolButton>
  )
}

// CTS-9209 — aria-pressed for toggle tools; CTS-9292 — optional aria-label (e.g. favorite + article title)
const ToolButton = ({
  icon,
  onClick,
  children,
  active = false,
  'aria-label': ariaLabel,
}) => {
  let className =
    'flex flex-col items-center justify-center cursor-pointer px-4'
  if (active) {
    className += ' bg-primary text-white rounded-2xl py-2 font-medium'
  }

  return (
    <button
      type="button"
      className={className}
      onClick={onClick}
      aria-pressed={active}
      {...(ariaLabel ? { 'aria-label': ariaLabel } : {})}
    >
      {icon}
      <span className="pt-2" {...(ariaLabel ? { 'aria-hidden': true } : {})}>
        {children}
      </span>
    </button>
  )
}

const ProgressDisplay = () => {
  const { t } = useTranslation('Controls')
  const { maxScrollDepth } = useArticle()

  return (
    <div className="flex flex-col items-center pr-4 pt-6">
      <Progress
        className="bg-[#BEE1FF]"
        indicatorClassName="bg-gradient-to-r from-[#08467C] to-[#4CACE8]"
        value={maxScrollDepth}
        aria-labelledby="progress-number-display"
      />
      <div id="progress-number-display" className="mt-2">
        {maxScrollDepth}% {t('completed')}
      </div>
    </div>
  )
}

const TableOfContents = () => {
  const { t } = useTranslation('Controls')
  const {
    jsonContent: { sections },
    jumpToSection,
  } = useArticle()

  return (
    <>
      <div className="font-bold">{t('table_of_contents')}</div>
      <ol className="list-outside list-decimal space-y-2 pl-4">
        {sections.map((section, index) => {
          if (!section.exclude_from_toc) {
            return (
              <li
                className="cursor-pointer text-balance break-words hover:text-primary hover:underline"
                key={index}
                onClick={() => jumpToSection(index)}
              >
                {section.tocTitle || section.title}
              </li>
            )
          }
        })}
      </ol>
    </>
  )
}

const BackToGuidesCliftonStrengths = () => {
  const { t } = useTranslation('Controls')
  const { navigateToGuides } = useArticle()
  return (
    <div className="!visible float-right mr-8 hidden h-20 place-content-center sm:!block">
      <button type="button" onClick={navigateToGuides}>
        {t('back_to_guides')}
        <BackArrow className="mb-1 ml-2 inline stroke-black" />
      </button>
    </div>
  )
}
